import type { LibrarySketch } from './types';

/**
 * Post-processing shader templates ported from the cloned reference repos.
 * All are GLSL fragment shaders that take `u_image` as input and can be
 * applied over any connected image or video node.
 *
 * Sources (see /shader-references/):
 *   - basementstudio/shader-lab (ASCII, halftone, dither, pixel-sort, CRT)
 *   - glslify/glsl-halftone
 *   - naughtyduk/glitchGL (Floyd-Steinberg-ish dither, Bayer)
 *   - luiscarlospando/crt-shader-with-chromatic-aberration-glow-scanlines-dot-matrix
 *   - gbendy/kaleidoscope + pishadertoy/shaders/kaleidoscope.f.glsl
 *   - Shadertoy edge detection (td2yDm) and oil paint (MtKcDG)
 *
 * All shaders use:
 *   vec2 uv = gl_FragCoord.xy / u_resolution;
 * (matching our GLSL bootstrap's vertex shader which passes only a_position).
 */
export const postEffectSketches: LibrarySketch[] = [
  // -------------------------------------------------------------------------
  // 1. ASCII (luminance → char grid)
  // -------------------------------------------------------------------------
  {
    id: 'post-ascii',
    name: 'ASCII Art',
    description: 'Renders the input image as a grid of ASCII characters based on per-cell luminance.',
    category: 'effects',
    tags: ['ascii', 'text', 'retro', 'grid', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'cellSize', type: 'float', min: 4.0, max: 24.0, default: 8.0, label: 'Cell Size' },
      { name: 'contrast', type: 'float', min: 0.5, max: 3.0, default: 1.2, label: 'Contrast' },
    ],
    code: `// @param float cellSize 4.0 24.0 8.0
// @param float contrast 0.5 3.0 1.2
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float cellSize;
uniform float contrast;

// Simple character-by-luminance 5x5 bitmask (dot, dash, plus, X, full)
float charMask(vec2 p, float lum) {
  p = fract(p) - 0.5;
  float r = length(p);
  if (lum < 0.15) return 0.0;
  if (lum < 0.30) return step(r, 0.1);          // dot
  if (lum < 0.50) return step(abs(p.y), 0.08);  // dash
  if (lum < 0.70) return max(step(abs(p.x), 0.08), step(abs(p.y), 0.08)); // plus
  if (lum < 0.85) return max(step(abs(p.x - p.y), 0.12), step(abs(p.x + p.y), 0.12)); // X
  return 1.0; // full block
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 cell = floor(gl_FragCoord.xy / cellSize) * cellSize / u_resolution;
  vec3 src = texture2D(u_image, cell).rgb;
  float lum = dot(src, vec3(0.299, 0.587, 0.114));
  lum = clamp((lum - 0.5) * contrast + 0.5, 0.0, 1.0);
  float m = charMask(gl_FragCoord.xy / cellSize, lum);
  gl_FragColor = vec4(vec3(m) * src, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 2. Halftone (CMYK-ish dot pattern)
  // -------------------------------------------------------------------------
  {
    id: 'post-halftone',
    name: 'Halftone Dots',
    description: 'Classic print-style halftone: luminance drives dot size on a rotated grid.',
    category: 'effects',
    tags: ['halftone', 'print', 'dots', 'newsprint', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'dotSize', type: 'float', min: 2.0, max: 20.0, default: 6.0, label: 'Dot Size' },
      { name: 'angle', type: 'float', min: 0.0, max: 1.57, default: 0.4, label: 'Angle' },
    ],
    code: `// @param float dotSize 2.0 20.0 6.0
// @param float angle 0.0 1.57 0.4
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float dotSize;
uniform float angle;

void main() {
  // Sample the source at this pixel's own UV — keeps the image aligned with input.
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 src = texture2D(u_image, uv).rgb;
  float lum = dot(src, vec3(0.299, 0.587, 0.114));

  // Rotation only drives the dot grid, not the texture sampling.
  float s = sin(angle), c = cos(angle);
  mat2 rot = mat2(c, -s, s, c);
  vec2 rc = rot * gl_FragCoord.xy;
  vec2 within = fract(rc / dotSize) - 0.5;
  float r = length(within);
  float dotRadius = sqrt(1.0 - lum) * 0.5;
  float mask = smoothstep(dotRadius + 0.04, dotRadius - 0.04, r);
  gl_FragColor = vec4(src * mask + (1.0 - mask) * 0.97, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 3. Bayer Dither (ordered, 4x4)
  // -------------------------------------------------------------------------
  {
    id: 'post-dither-bayer',
    name: 'Bayer Dither',
    description: 'Ordered 4x4 Bayer-matrix dithering — the classic "old Mac" quantization look.',
    category: 'effects',
    tags: ['dither', 'bayer', 'retro', 'quantize', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'levels', type: 'int', min: 2, max: 16, default: 4, label: 'Levels' },
      { name: 'scale', type: 'float', min: 1.0, max: 8.0, default: 1.0, label: 'Scale' },
    ],
    code: `// @param int levels 2 16 4
// @param float scale 1.0 8.0 1.0
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float levels;
uniform float scale;

float bayer4(vec2 p) {
  int x = int(mod(p.x, 4.0));
  int y = int(mod(p.y, 4.0));
  int idx = x + y * 4;
  if (idx == 0)  return 0.0/16.0; if (idx == 1)  return 8.0/16.0;
  if (idx == 2)  return 2.0/16.0; if (idx == 3)  return 10.0/16.0;
  if (idx == 4)  return 12.0/16.0; if (idx == 5) return 4.0/16.0;
  if (idx == 6)  return 14.0/16.0; if (idx == 7) return 6.0/16.0;
  if (idx == 8)  return 3.0/16.0; if (idx == 9)  return 11.0/16.0;
  if (idx == 10) return 1.0/16.0; if (idx == 11) return 9.0/16.0;
  if (idx == 12) return 15.0/16.0; if (idx == 13) return 7.0/16.0;
  if (idx == 14) return 13.0/16.0;
  return 5.0/16.0;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 src = texture2D(u_image, uv).rgb;
  float t = bayer4(floor(gl_FragCoord.xy / scale));
  vec3 q = floor(src * levels + t) / levels;
  gl_FragColor = vec4(q, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 4. Pixelation
  // -------------------------------------------------------------------------
  {
    id: 'post-pixelate',
    name: 'Pixelate',
    description: 'Mosaic block pixelation — chunky low-res look.',
    category: 'effects',
    tags: ['pixelate', 'mosaic', 'blocky', 'low-res', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'blockSize', type: 'float', min: 2.0, max: 64.0, default: 12.0, label: 'Block Size' },
    ],
    code: `// @param float blockSize 2.0 64.0 12.0
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float blockSize;

void main() {
  vec2 block = floor(gl_FragCoord.xy / blockSize) * blockSize + blockSize * 0.5;
  vec2 uv = block / u_resolution;
  gl_FragColor = texture2D(u_image, uv);
}`,
  },

  // -------------------------------------------------------------------------
  // 5. Edge Detection (Sobel)
  // -------------------------------------------------------------------------
  {
    id: 'post-edge-sobel',
    name: 'Edge Detect (Sobel)',
    description: 'Classic Sobel operator — isolates outlines from the input image.',
    category: 'effects',
    tags: ['edge', 'sobel', 'outline', 'convolution', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'thickness', type: 'float', min: 0.5, max: 5.0, default: 1.0, label: 'Thickness' },
      { name: 'threshold', type: 'float', min: 0.0, max: 1.0, default: 0.15, label: 'Threshold' },
      { name: 'invert', type: 'bool', default: false, label: 'Invert' },
    ],
    code: `// @param float thickness 0.5 5.0 1.0
// @param float threshold 0.0 1.0 0.15
// @param bool invert false
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float thickness;
uniform float threshold;
uniform bool invert;

float lum(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 px = thickness / u_resolution;
  float tl = lum(texture2D(u_image, uv + vec2(-px.x,  px.y)).rgb);
  float tc = lum(texture2D(u_image, uv + vec2( 0.0,   px.y)).rgb);
  float tr = lum(texture2D(u_image, uv + vec2( px.x,  px.y)).rgb);
  float ml = lum(texture2D(u_image, uv + vec2(-px.x,  0.0)).rgb);
  float mr = lum(texture2D(u_image, uv + vec2( px.x,  0.0)).rgb);
  float bl = lum(texture2D(u_image, uv + vec2(-px.x, -px.y)).rgb);
  float bc = lum(texture2D(u_image, uv + vec2( 0.0,  -px.y)).rgb);
  float br = lum(texture2D(u_image, uv + vec2( px.x, -px.y)).rgb);
  float gx = -tl - 2.0*ml - bl + tr + 2.0*mr + br;
  float gy = -tl - 2.0*tc - tr + bl + 2.0*bc + br;
  float g = length(vec2(gx, gy));
  float e = smoothstep(threshold, threshold + 0.1, g);
  vec3 col = invert ? vec3(1.0 - e) : vec3(e);
  gl_FragColor = vec4(col, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 6. Kaleidoscope
  // -------------------------------------------------------------------------
  {
    id: 'post-kaleidoscope',
    name: 'Kaleidoscope',
    description: 'Angular mirror repetition — folds the input image into N symmetric wedges.',
    category: 'effects',
    tags: ['kaleidoscope', 'symmetry', 'mirror', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'slices', type: 'int', min: 2, max: 24, default: 6, label: 'Slices' },
      { name: 'rotation', type: 'float', min: 0.0, max: 6.28, default: 0.0, label: 'Rotation' },
      { name: 'zoom', type: 'float', min: 0.3, max: 2.0, default: 1.0, label: 'Zoom' },
    ],
    code: `// @param int slices 2 24 6
// @param float rotation 0.0 6.28 0.0
// @param float zoom 0.3 2.0 1.0
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float slices;
uniform float rotation;
uniform float zoom;

void main() {
  vec2 uv = (gl_FragCoord.xy - 0.5 * u_resolution) / min(u_resolution.x, u_resolution.y);
  uv /= zoom;
  float r = length(uv);
  float a = atan(uv.y, uv.x) + rotation;
  float wedge = 6.28318 / slices;
  a = mod(a, wedge);
  a = abs(a - wedge * 0.5);
  vec2 folded = vec2(cos(a), sin(a)) * r;
  vec2 sampleUv = folded + 0.5;
  sampleUv = clamp(sampleUv, vec2(0.0), vec2(1.0));
  gl_FragColor = texture2D(u_image, sampleUv);
}`,
  },

  // -------------------------------------------------------------------------
  // 7. CRT (scanlines + vignette + chromatic aberration + curvature)
  // -------------------------------------------------------------------------
  {
    id: 'post-crt',
    name: 'CRT Monitor',
    description: 'Vintage CRT look: barrel distortion, scanlines, chromatic aberration, vignette.',
    category: 'effects',
    tags: ['crt', 'scanlines', 'retro', 'vhs', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'curvature', type: 'float', min: 0.0, max: 0.5, default: 0.1, label: 'Curvature' },
      { name: 'scanlines', type: 'float', min: 0.0, max: 1.0, default: 0.4, label: 'Scanlines' },
      { name: 'aberration', type: 'float', min: 0.0, max: 0.02, default: 0.004, label: 'Aberration' },
      { name: 'vignette', type: 'float', min: 0.0, max: 1.0, default: 0.35, label: 'Vignette' },
    ],
    code: `// @param float curvature 0.0 0.5 0.1
// @param float scanlines 0.0 1.0 0.4
// @param float aberration 0.0 0.02 0.004
// @param float vignette 0.0 1.0 0.35
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_time;
uniform float curvature;
uniform float scanlines;
uniform float aberration;
uniform float vignette;

vec2 curve(vec2 uv) {
  uv = uv * 2.0 - 1.0;
  vec2 offset = abs(uv.yx) / vec2(6.0 / max(curvature, 0.0001), 4.0 / max(curvature, 0.0001));
  uv = uv + uv * offset * offset;
  return uv * 0.5 + 0.5;
}

void main() {
  vec2 uv = curve(gl_FragCoord.xy / u_resolution);
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0);
    return;
  }
  float r = texture2D(u_image, uv + vec2(aberration,  0.0)).r;
  float g = texture2D(u_image, uv).g;
  float b = texture2D(u_image, uv - vec2(aberration,  0.0)).b;
  vec3 col = vec3(r, g, b);
  float scan = sin(uv.y * u_resolution.y * 1.8) * 0.5 + 0.5;
  col *= 1.0 - scanlines * (1.0 - scan);
  vec2 v = uv - 0.5;
  float vig = 1.0 - dot(v, v) * vignette * 4.0;
  col *= clamp(vig, 0.0, 1.0);
  gl_FragColor = vec4(col, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 8. Chromatic Aberration
  // -------------------------------------------------------------------------
  {
    id: 'post-chromatic-aberration',
    name: 'Chromatic Aberration',
    description: 'Radial RGB channel separation — lens distortion fringing.',
    category: 'effects',
    tags: ['chromatic', 'aberration', 'lens', 'glitch', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'strength', type: 'float', min: 0.0, max: 0.1, default: 0.04, label: 'Strength' },
    ],
    code: `// @param float strength 0.0 0.1 0.04
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float strength;

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 dir = uv - 0.5;
  float r = texture2D(u_image, uv + dir * strength).r;
  float g = texture2D(u_image, uv).g;
  float b = texture2D(u_image, uv - dir * strength).b;
  gl_FragColor = vec4(r, g, b, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 9. Oil Paint (Kuwahara-ish simplified)
  // -------------------------------------------------------------------------
  {
    id: 'post-oil-paint',
    name: 'Oil Paint',
    description: 'Painterly Kuwahara-style smoothing — picks the lowest-variance neighborhood per pixel.',
    category: 'effects',
    tags: ['oil', 'paint', 'kuwahara', 'painterly', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'radius', type: 'int', min: 2, max: 8, default: 6, label: 'Brush Radius' },
    ],
    code: `// @param int radius 2 8 6
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform int radius;

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec2 px = 1.0 / u_resolution;
  vec3 best = texture2D(u_image, uv).rgb;
  float bestVar = 1e9;
  // 4 quadrants around the pixel; pick the one with lowest variance
  for (int q = 0; q < 4; q++) {
    vec2 dir = vec2(q == 0 || q == 3 ? -1.0 : 1.0, q < 2 ? -1.0 : 1.0);
    vec3 mean = vec3(0.0);
    vec3 meanSq = vec3(0.0);
    float n = 0.0;
    for (int i = 0; i <= 8; i++) {
      if (i > radius) break;
      for (int j = 0; j <= 8; j++) {
        if (j > radius) break;
        vec2 off = vec2(float(i), float(j)) * dir * px * 2.0;
        vec3 c = texture2D(u_image, uv + off).rgb;
        mean += c;
        meanSq += c * c;
        n += 1.0;
      }
    }
    mean /= n;
    meanSq /= n;
    vec3 variance = meanSq - mean * mean;
    float totalVar = variance.r + variance.g + variance.b;
    if (totalVar < bestVar) { bestVar = totalVar; best = mean; }
  }
  gl_FragColor = vec4(best, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 10. Posterize
  // -------------------------------------------------------------------------
  {
    id: 'post-posterize',
    name: 'Posterize',
    description: 'Reduces color depth per channel for a flat, graphic poster look.',
    category: 'effects',
    tags: ['posterize', 'quantize', 'graphic', 'flat', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'steps', type: 'int', min: 2, max: 16, default: 5, label: 'Steps' },
      { name: 'gamma', type: 'float', min: 0.3, max: 3.0, default: 1.0, label: 'Gamma' },
    ],
    code: `// @param int steps 2 16 5
// @param float gamma 0.3 3.0 1.0
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float steps;
uniform float gamma;

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 src = texture2D(u_image, uv).rgb;
  vec3 g = pow(src, vec3(gamma));
  vec3 q = floor(g * steps) / (steps - 1.0);
  gl_FragColor = vec4(pow(q, vec3(1.0 / gamma)), 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 11. Pixel Sort (vertical, brightness-based)
  // -------------------------------------------------------------------------
  {
    id: 'post-pixel-sort',
    name: 'Pixel Sort (Vertical)',
    description: 'Simulated vertical pixel-sort glitch — bright columns stretch downward.',
    category: 'effects',
    tags: ['glitch', 'sort', 'datamosh', 'vertical', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'threshold', type: 'float', min: 0.0, max: 1.0, default: 0.3, label: 'Threshold' },
      { name: 'streakLength', type: 'float', min: 0.01, max: 0.5, default: 0.25, label: 'Streak Length' },
    ],
    code: `// @param float threshold 0.0 1.0 0.3
// @param float streakLength 0.01 0.5 0.25
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float threshold;
uniform float streakLength;

float lum(vec3 c) { return dot(c, vec3(0.299, 0.587, 0.114)); }

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 src = texture2D(u_image, uv).rgb;
  // Walk upward until luminance drops below threshold; use that pixel
  vec3 best = src;
  float bestL = lum(src);
  for (int i = 0; i < 32; i++) {
    float offset = float(i) / 32.0 * streakLength;
    vec2 q = vec2(uv.x, uv.y + offset);
    if (q.y > 1.0) break;
    vec3 s = texture2D(u_image, q).rgb;
    float sl = lum(s);
    if (sl < threshold) break;
    if (sl > bestL) { best = s; bestL = sl; }
  }
  gl_FragColor = vec4(best, 1.0);
}`,
  },

  // -------------------------------------------------------------------------
  // 12. Invert
  // -------------------------------------------------------------------------
  {
    id: 'post-invert',
    name: 'Invert',
    description: 'Per-channel color inversion with optional luminance-only mode.',
    category: 'effects',
    tags: ['invert', 'negative', 'utility', 'post'],
    runtimeMode: 'glsl',
    inputTypes: ['image'],
    params: [
      { name: 'amount', type: 'float', min: 0.0, max: 1.0, default: 1.0, label: 'Amount' },
      { name: 'luminanceOnly', type: 'bool', default: false, label: 'Luminance Only' },
    ],
    code: `// @param float amount 0.0 1.0 1.0
// @param bool luminanceOnly false
precision mediump float;
uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float amount;
uniform bool luminanceOnly;

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  vec3 src = texture2D(u_image, uv).rgb;
  vec3 inv;
  if (luminanceOnly) {
    float l = dot(src, vec3(0.299, 0.587, 0.114));
    inv = src + (1.0 - 2.0 * l);
    inv = clamp(inv, 0.0, 1.0);
  } else {
    inv = 1.0 - src;
  }
  gl_FragColor = vec4(src + (inv - src) * amount, 1.0);
}`,
  },
];
